import { Injectable } from '@angular/core';
import { ToastService } from '@msi/cobalt-prime';

/** Local toast model used by callers across the application. */
export interface Toast {
  message: string;
}

/** Optional action button rendered alongside the toast message. */
export interface ToastButton {
  text: string;
  click?: () => unknown;
}

/** Per-call toast behavior overrides. */
export interface ToastOptions {
  autoDismiss?: boolean | number;
  closeButton?: boolean;
  onRemove?: (toast: Toast) => void;
}

type ToastSeverity = 'success' | 'warning' | 'error' | 'information';

/**
 * Centralized toast service manager that wraps cobalt-prime's {@link ToastService}
 * to provide consistent timing and a stable API across the application.
 */
@Injectable({
  providedIn: 'root',
})
export class ToastServiceManager {
  private readonly SUCCESS_DURATION_MS = 5000;
  private readonly WARNING_DURATION_MS = 8000;
  private readonly ERROR_DURATION_MS = 13000;
  private readonly CRITICAL_ERROR_DURATION_MS = 20000;

  constructor(private readonly toastService: ToastService) {}

  public success(message: string, button?: ToastButton, options?: ToastOptions): Toast {
    return this.showToast('success', message, this.SUCCESS_DURATION_MS, button, options);
  }

  public warning(message: string, button?: ToastButton, options?: ToastOptions): Toast {
    return this.showToast('warning', message, this.WARNING_DURATION_MS, button, options);
  }

  public error(message: string, button?: ToastButton, options?: ToastOptions): Toast {
    return this.showToast('error', message, this.ERROR_DURATION_MS, button, options);
  }

  public criticalError(message: string, button?: ToastButton, options?: ToastOptions): Toast {
    return this.showToast('error', message, this.CRITICAL_ERROR_DURATION_MS, button, options);
  }

  public information(message: string, button?: ToastButton, options?: ToastOptions): Toast {
    return this.showToast('information', message, undefined, button, {
      closeButton: options?.closeButton ?? true,
      ...options,
    });
  }

  private showToast(
    severity: ToastSeverity,
    message: string,
    defaultDurationMs: number | undefined,
    button?: ToastButton,
    options?: ToastOptions,
  ): Toast {
    const toast: Toast = { message };

    const autoDismiss = options?.autoDismiss;
    const sticky =
      severity === 'information'
        ? options?.autoDismiss !== true && typeof options?.autoDismiss !== 'number'
        : autoDismiss === false;

    let life: number | undefined;
    if (!sticky) {
      if (typeof autoDismiss === 'number' && autoDismiss > (defaultDurationMs ?? 0)) {
        life = autoDismiss;
      } else if (defaultDurationMs !== undefined) {
        life = defaultDurationMs;
      } else if (typeof autoDismiss === 'number') {
        life = autoDismiss;
      }
    }

    const onClose = (): void => {
      options?.onRemove?.(toast);
    };

    const toastOptions: Record<string, unknown> = {
      summary: message,
      sticky,
      closable: options?.closeButton ?? false,
      onClose,
    };
    if (life !== undefined) {
      toastOptions['life'] = life;
    }
    if (button) {
      toastOptions['customButtonLabel'] = button.text;
      toastOptions['onCustomAction'] = button.click;
    }

    switch (severity) {
      case 'success':
        this.toastService.success(toastOptions as never);
        break;
      case 'warning':
        this.toastService.warning(toastOptions as never);
        break;
      case 'error':
        this.toastService.error(toastOptions as never);
        break;
      case 'information':
        this.toastService.information(toastOptions as never);
        break;
    }

    return toast;
  }
}
